/**
 * useProactiveGuide — Rule-based engine that computes 2-3 contextual guide cards
 * from existing journey + entity data. Pure computation, no API calls.
 */

import { useMemo } from 'react'
import { useJourneyStore } from '@/stores/journeyStore'
import { useSkerpaContextStore, type CanvasSectionState } from '@/stores/skerpaContextStore'
import { useSkerpaEntitySummary } from '@/hooks/useSkerpaEntitySummary'
import type { GuideCard, GuideDomain } from '@ispot/shared'

// ── Context shape for rule evaluation ──

interface GuideContext {
  activeView: string
  overallProgress: number
  domainScores: { market: number; solution: number; business: number }
  currentPhaseName: string | null
  currentPhaseDomain: string | null
  incompleteActivities: Array<{ id: string; name: string; description?: string }>
  gateCriteria: string[]
  entityCounts: { market: number; business: number; solution: number; total: number }
  hasGoal: boolean
  canvasSectionState: CanvasSectionState | null
}

// ── Rule definition ──

interface GuideRule {
  id: string
  priority: number
  condition: (ctx: GuideContext) => boolean
  card: (ctx: GuideContext) => GuideCard
}

// ── View → domain boost mapping ──

const VIEW_DOMAIN_MAP: Record<string, GuideDomain> = {
  'persona-configurator': 'market',
  'persona-workshop': 'market',
  'value-design': 'market',
  'canvas-market': 'market',
  'market-segmentation': 'market',
  'competitor-map': 'business',
  'canvas-business': 'business',
  'canvas-solution': 'solution',
  'solution-configurator': 'solution',
  'solution-workspace': 'solution',
}

// ── Rule definitions (lower priority number = higher rank) ──

const GUIDE_RULES: GuideRule[] = [
  // No goal selected — blocks everything
  {
    id: 'no-goal',
    priority: 0,
    condition: (ctx) => !ctx.hasGoal,
    card: () => ({
      id: 'no-goal',
      title: 'Choose Your Journey Path',
      description: 'Select a goal to unlock your personalized product journey with guided phases and activities.',
      urgency: 'critical',
      domain: 'general',
      icon: 'compass',
      prompt: 'Help me choose the right journey goal for my project. What are the options and which one fits best?',
      viewTarget: 'workspace',
    }),
  },

  // ── View-specific rules (highest priority when on the view) ──

  {
    id: 'view-persona',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'persona-configurator' || ctx.activeView === 'persona-workshop',
    card: () => ({
      id: 'view-persona',
      title: 'Help me with this persona',
      description: 'Review pains, gains, and jobs. Suggest what\'s missing based on my project data.',
      urgency: 'high',
      domain: 'market',
      icon: 'users',
      prompt: 'Look at the persona I\'m editing right now. What pains, gains, or jobs am I missing? Are they specific enough?',
    }),
  },
  {
    id: 'view-competitor',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'competitor-map',
    card: () => ({
      id: 'view-competitor',
      title: 'Help me with competitors',
      description: 'Analyze gaps, suggest missing competitors, or categorize my landscape.',
      urgency: 'high',
      domain: 'business',
      icon: 'shield',
      prompt: 'Look at my competitor map. What gaps or opportunities do you see? Are there competitors I\'m missing?',
    }),
  },
  {
    id: 'view-value-design',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'value-design',
    card: () => ({
      id: 'view-value-design',
      title: 'Help me with my value proposition',
      description: 'Review pain relievers, gain creators, and overall value fit.',
      urgency: 'high',
      domain: 'market',
      icon: 'target',
      prompt: 'Look at my value proposition canvas. Are the pain relievers and gain creators strong enough? What\'s missing?',
    }),
  },
  {
    id: 'view-solution',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'solution-workspace' || ctx.activeView === 'solution-configurator',
    card: () => ({
      id: 'view-solution',
      title: 'Help me with my solution',
      description: 'Review vision, features, and requirements. Ensure they trace to real pains.',
      urgency: 'high',
      domain: 'solution',
      icon: 'lightbulb',
      prompt: 'Look at my solution workspace. Are features linked to validated pains? What should I prioritize?',
    }),
  },
  {
    id: 'view-segments',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'market-segmentation',
    card: () => ({
      id: 'view-segments',
      title: 'Help me with my segments',
      description: 'Review sizing, scoring, and buyer structure grounded in your project data.',
      urgency: 'high',
      domain: 'market',
      icon: 'layout-list',
      prompt: 'Look at my market segments. Are they well-defined and prioritized? What sizing or scoring is missing?',
    }),
  },
  {
    id: 'view-problem-statement',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'canvas-solution',
    card: () => ({
      id: 'view-problem-statement',
      title: 'Help me with my problem statement',
      description: 'Review how clearly the problem is framed for your target personas.',
      urgency: 'high',
      domain: 'solution',
      icon: 'lightbulb',
      prompt: 'Look at my problem statement canvas. Is the problem clearly framed? Does it connect to validated pains from my personas?',
    }),
  },
  {
    id: 'view-requirements',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'requirements',
    card: () => ({
      id: 'view-requirements',
      title: 'Help me with my requirements',
      description: 'Review user stories and acceptance criteria. Check traceability to real pains.',
      urgency: 'high',
      domain: 'solution',
      icon: 'clipboard-list',
      prompt: 'Look at my requirements. Are the user stories specific and traceable to validated persona pains? What is missing or unclear?',
    }),
  },
  {
    id: 'view-validation',
    priority: 1,
    condition: (ctx) => ctx.activeView === 'analysis',
    card: () => ({
      id: 'view-validation',
      title: 'Help me with validation',
      description: 'Review evidence scores and identify which assumptions still need testing.',
      urgency: 'high',
      domain: 'business',
      icon: 'badge-check',
      prompt: 'Look at my validation analysis. Which assumptions are weakest? What experiments or interviews would strengthen my evidence?',
    }),
  },

  // Canvas with empty sections — suggest filling them
  {
    id: 'view-canvas-empty-sections',
    priority: 1,
    condition: (ctx) =>
      ctx.activeView.startsWith('canvas-') &&
      !!ctx.canvasSectionState &&
      ctx.canvasSectionState.emptySections.length > 0,
    card: (ctx) => {
      const state = ctx.canvasSectionState!
      const emptyList = state.emptySections.slice(0, 3).join(', ')
      const more = state.emptySections.length > 3 ? ` and ${state.emptySections.length - 3} more` : ''
      const domain = (VIEW_DOMAIN_MAP[ctx.activeView] || 'business') as GuideDomain
      return {
        id: 'view-canvas-empty-sections',
        title: `Fill empty sections`,
        description: `${state.emptySections.length} of ${state.totalSections} sections need content: ${emptyList}${more}.`,
        urgency: 'high',
        domain,
        icon: 'layout',
        prompt: `Look at my canvas. These sections are still empty: ${state.emptySections.join(', ')}. Based on my project data, what should go in each one?`,
      }
    },
  },

  // ── Journey-level rules ──

  // Zero market entities — no personas, segments, etc.
  {
    id: 'no-market-entities',
    priority: 10,
    condition: (ctx) => ctx.hasGoal && ctx.entityCounts.market === 0,
    card: () => ({
      id: 'no-market-entities',
      title: 'Define Your First Persona',
      description: 'Start by identifying who you\'re building for. Personas ground every product decision in real user needs.',
      urgency: 'critical',
      domain: 'market',
      icon: 'users',
      prompt: 'Help me create my first user persona. Who should I be thinking about as my primary user?',
      viewTarget: 'persona-workshop',
    }),
  },

  // Very few market entities — need more depth
  {
    id: 'thin-market',
    priority: 20,
    condition: (ctx) => ctx.hasGoal && ctx.entityCounts.market > 0 && ctx.entityCounts.market < 5 && ctx.domainScores.market < 30,
    card: (ctx) => ({
      id: 'thin-market',
      title: 'Deepen Market Understanding',
      description: `You have ${ctx.entityCounts.market} market entities. Add more personas, pains, and jobs to build a clearer picture.`,
      urgency: 'high',
      domain: 'market',
      icon: 'search',
      prompt: 'What market entities am I missing? Help me identify gaps in my persona research and customer understanding.',
    }),
  },

  // No business entities — no competitors, strategies, etc.
  {
    id: 'no-business-entities',
    priority: 15,
    condition: (ctx) => ctx.hasGoal && ctx.entityCounts.business === 0 && ctx.entityCounts.market >= 3,
    card: () => ({
      id: 'no-business-entities',
      title: 'Map Your Competitive Landscape',
      description: 'You have personas defined. Now map competitors to understand your positioning opportunity.',
      urgency: 'high',
      domain: 'business',
      icon: 'target',
      prompt: 'Help me map my competitive landscape. Who are my main competitors and how should I position against them?',
      viewTarget: 'competitor-map',
    }),
  },

  // No solution entities — market + business exist but no solution work
  {
    id: 'no-solution-entities',
    priority: 25,
    condition: (ctx) => ctx.hasGoal && ctx.entityCounts.solution === 0 && ctx.entityCounts.market >= 3 && ctx.domainScores.market >= 30,
    card: () => ({
      id: 'no-solution-entities',
      title: 'Start Defining Your Solution',
      description: 'Your market research is taking shape. Start translating insights into features and requirements.',
      urgency: 'medium',
      domain: 'solution',
      icon: 'lightbulb',
      prompt: 'Based on my personas and market research, help me start defining the solution. What features should I prioritize?',
      viewTarget: 'solution-workspace',
    }),
  },

  // Low market score but has some entities
  {
    id: 'low-market-score',
    priority: 30,
    condition: (ctx) => ctx.hasGoal && ctx.domainScores.market < 40 && ctx.domainScores.market > 0 && ctx.entityCounts.market >= 5,
    card: (ctx) => ({
      id: 'low-market-score',
      title: 'Strengthen Market Evidence',
      description: `Market score is ${ctx.domainScores.market}%. Upload documents or conduct interviews to validate your assumptions.`,
      urgency: 'medium',
      domain: 'market',
      icon: 'file-text',
      prompt: 'My market understanding needs more evidence. What documents should I upload or interviews should I conduct?',
      viewTarget: 'sources',
    }),
  },

  // Phase has incomplete activities
  {
    id: 'incomplete-activities',
    priority: 35,
    condition: (ctx) => ctx.incompleteActivities.length > 0,
    card: (ctx) => {
      const next = ctx.incompleteActivities[0]
      return {
        id: 'incomplete-activities',
        title: next.name,
        description: next.description || `Complete this activity to advance in the ${ctx.currentPhaseName} phase.`,
        urgency: 'medium',
        domain: (ctx.currentPhaseDomain as GuideDomain) || 'general',
        icon: 'check-circle',
        prompt: `Help me with "${next.name}". What should I do to complete this activity?`,
      }
    },
  },

  // Good market + business, low solution
  {
    id: 'solution-lagging',
    priority: 40,
    condition: (ctx) => ctx.hasGoal && ctx.domainScores.market >= 50 && ctx.domainScores.business >= 30 && ctx.domainScores.solution < 20,
    card: () => ({
      id: 'solution-lagging',
      title: 'Translate Research Into Solutions',
      description: 'Your market and business understanding is strong. Time to design the solution architecture.',
      urgency: 'medium',
      domain: 'solution',
      icon: 'puzzle',
      prompt: 'I have good market and business understanding. Help me translate this into a concrete solution design.',
      viewTarget: 'solution-workspace',
    }),
  },

  // General: upload documents when nothing uploaded
  {
    id: 'no-documents',
    priority: 50,
    condition: (ctx) => ctx.hasGoal && ctx.entityCounts.total === 0 && ctx.overallProgress < 10,
    card: () => ({
      id: 'no-documents',
      title: 'Upload Your Research',
      description: 'Start by uploading existing research, interviews, or market data. Skerpa will extract insights automatically.',
      urgency: 'medium',
      domain: 'general',
      icon: 'upload',
      prompt: 'What types of documents should I upload to get the most value? Help me plan my research import.',
      viewTarget: 'sources',
    }),
  },

  // Well-progressed: review overall progress
  {
    id: 'review-progress',
    priority: 60,
    condition: (ctx) => ctx.overallProgress >= 60,
    card: (ctx) => ({
      id: 'review-progress',
      title: 'Review Your Progress',
      description: `You're ${ctx.overallProgress}% through your journey. Let's identify remaining gaps and prioritize next steps.`,
      urgency: 'low',
      domain: 'general',
      icon: 'bar-chart',
      prompt: 'Review my overall project progress. What are the biggest gaps and what should I focus on next?',
      viewTarget: 'workspace',
    }),
  },

  // Current phase guidance — always available when a phase is active
  {
    id: 'current-phase',
    priority: 45,
    condition: (ctx) => ctx.hasGoal && !!ctx.currentPhaseName,
    card: (ctx) => ({
      id: 'current-phase',
      title: `Continue: ${ctx.currentPhaseName}`,
      description: `You're in the ${ctx.currentPhaseName} phase. Ask me what to focus on next based on your current progress.`,
      urgency: 'medium',
      domain: (ctx.currentPhaseDomain as GuideDomain) || 'general',
      icon: 'compass',
      prompt: `I'm in the ${ctx.currentPhaseName} phase. Based on my project data, what should I work on next?`,
    }),
  },

  // Catch-all: always offer help when nothing else matches
  {
    id: 'ask-anything',
    priority: 100,
    condition: () => true,
    card: () => ({
      id: 'ask-anything',
      title: 'Ask me anything about your product',
      description: 'I can help with personas, competitors, positioning, features, or any product question.',
      urgency: 'low',
      domain: 'general',
      icon: 'compass',
      prompt: 'Based on everything you know about my project, what are the most important things I should focus on right now?',
    }),
  },
]

// ── Hook ──

export function useProactiveGuide(): { cards: GuideCard[]; isReady: boolean } {
  const framework = useJourneyStore(s => s.framework)
  const journey = useJourneyStore(s => s.journey)
  const getCurrentPhase = useJourneyStore(s => s.getCurrentPhase)
  const getDomainScores = useJourneyStore(s => s.getDomainScores)
  const getOverallProgress = useJourneyStore(s => s.getOverallProgress)
  const isActivityCompleted = useJourneyStore(s => s.isActivityCompleted)
  const hasGoal = useJourneyStore(s => s.hasGoal)
  const activeView = useSkerpaContextStore(s => s.activeView)
  const canvasSectionState = useSkerpaContextStore(s => s.canvasSectionState)
  const entitySummary = useSkerpaEntitySummary()

  const isReady = !!(framework && journey)

  const cards = useMemo(() => {
    if (!isReady) return []

    // Parse entity counts from summary string
    const entityCounts = parseEntityCounts(entitySummary)

    // Build evaluation context
    const currentPhase = getCurrentPhase()
    const incompleteActivities = (currentPhase?.activities || [])
      .filter(a => !isActivityCompleted(a.id))
      .sort((a, b) => a.order - b.order)

    const ctx: GuideContext = {
      activeView,
      overallProgress: getOverallProgress(),
      domainScores: getDomainScores(),
      currentPhaseName: currentPhase?.name || null,
      currentPhaseDomain: currentPhase?.domain || null,
      incompleteActivities,
      gateCriteria: currentPhase?.gate?.criteria || [],
      entityCounts,
      hasGoal: hasGoal(),
      canvasSectionState,
    }

    // Evaluate all rules
    const matching = GUIDE_RULES
      .filter(rule => rule.condition(ctx))
      .map(rule => ({
        card: rule.card(ctx),
        priority: rule.priority + viewBoost(rule.card(ctx).domain, activeView),
      }))
      .sort((a, b) => a.priority - b.priority)

    // Return top 3
    return matching.slice(0, 3).map(m => m.card)
  }, [isReady, entitySummary, activeView, canvasSectionState, getCurrentPhase, getDomainScores, getOverallProgress, isActivityCompleted, hasGoal])

  return { cards, isReady }
}

// ── Helpers ──

function viewBoost(cardDomain: GuideDomain, activeView: string): number {
  const viewDomain = VIEW_DOMAIN_MAP[activeView]
  return cardDomain === viewDomain ? -10 : 0
}

function parseEntityCounts(summary: string): GuideContext['entityCounts'] {
  const counts = { market: 0, business: 0, solution: 0, total: 0 }
  if (!summary) return counts

  // Extract total from "Total entities: N"
  const totalMatch = summary.match(/Total entities: (\d+)/)
  if (totalMatch) counts.total = parseInt(totalMatch[1], 10)

  // Count entities per domain from "DOMAIN DOMAIN:" sections
  const domainPattern = /^(MARKET|BUSINESS|SOLUTION) DOMAIN:/gm
  let match
  while ((match = domainPattern.exec(summary)) !== null) {
    const domain = match[1].toLowerCase() as 'market' | 'business' | 'solution'
    // Count instances in lines after this domain header until next domain or end
    const afterDomain = summary.slice(match.index + match[0].length)
    const nextDomainIndex = afterDomain.search(/^(MARKET|BUSINESS|SOLUTION) DOMAIN:/m)
    const domainBlock = nextDomainIndex >= 0 ? afterDomain.slice(0, nextDomainIndex) : afterDomain
    // Each line like "  ClassName (N):" has a count in parens
    const countMatches = domainBlock.matchAll(/\((\d+)\)/g)
    for (const cm of countMatches) {
      counts[domain] += parseInt(cm[1], 10)
    }
  }

  return counts
}
